import React, { useEffect, useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import type { PropertyRoom, RoomScore, SourceRoom } from './types';

interface Props {
  sourceRoom: SourceRoom;
  candidateRoomNames: Array<PropertyRoom>;
  onQuickMap: (sourceRoom: SourceRoom, targetPropertyRoomName: string) => void;
  isQuickMapping?: boolean;
  onUpdateMLMatchScore: (sourceRoom: SourceRoom, bestScore: number) => void;
  appliedButNotSavedRooms?: Array<SourceRoom>;
  mlMatchingData?: {
    ranking: Array<RoomScore>;
    confidenceThreshold: number;
  };
  isLoadingMLCandidates?: boolean;
}

export default function RoomMatchingSuggestions({
  sourceRoom,
  candidateRoomNames,
  onQuickMap,
  isQuickMapping,
  onUpdateMLMatchScore,
  mlMatchingData,
  isLoadingMLCandidates = false,
}: Props) {
  // Memoize the candidate room names to prevent unnecessary re-renders
  const stableCandidateNames = useMemo(() => {
    return candidateRoomNames.map((room) => room.name);
  }, [candidateRoomNames]);

  const confidenceThreshold = mlMatchingData?.confidenceThreshold;

  const suggestions = useMemo(() => {
    if (!mlMatchingData) {
      return [];
    }

    return mlMatchingData.ranking.filter((suggestion) => stableCandidateNames.includes(suggestion.roomName));
  }, [mlMatchingData, stableCandidateNames]);

  // Update ML match score using the top filtered suggestion (not the unfiltered ranking)
  // This ensures the score reflects only mappable candidates
  useEffect(() => {
    if (suggestions.length > 0) {
      onUpdateMLMatchScore(sourceRoom, suggestions[0].score);
    }
  }, [suggestions, sourceRoom, onUpdateMLMatchScore]);

  const renderSuggestion = (suggestion: RoomScore) => {
    const confidencePercent = (suggestion.score * 100).toFixed(1);
    const isHighConfidence = suggestion.score > confidenceThreshold;
    const jaccardPercent = (suggestion.jaccard * 100).toFixed(0);

    return (
      <Paper
        key={suggestion.roomName}
        variant="outlined"
        sx={{
          p: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderColor: isHighConfidence ? 'success.light' : 'warning.light',
          bgcolor: 'transparent',
        }}
      >
        <Typography variant="body2" sx={{ mr: 1, flex: 1 }}>
          {suggestion.roomName}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip label={`${confidencePercent}% match`} color={isHighConfidence ? 'success' : 'warning'} size="small" />
          <Chip
            label={`Jaccard ${jaccardPercent}%`}
            variant="outlined"
            size="small"
            title="Token overlap between source and master name (independent of ML score)"
          />
          <Chip
            label="Map"
            clickable
            onClick={() => onQuickMap(sourceRoom, suggestion.roomName)}
            color="primary"
            size="small"
            disabled={isQuickMapping}
            aria-label={`Map ${sourceRoom.name} to ${suggestion.roomName}`}
            role="button"
            tabIndex={0}
            sx={{
              cursor: isQuickMapping ? 'not-allowed' : 'pointer',
              opacity: isQuickMapping ? 0.5 : 1,
            }}
          />
        </Stack>
      </Paper>
    );
  };

  const topSuggestions = suggestions.slice(0, 2);
  const remainingSuggestions = suggestions.slice(2);

  if (!isLoadingMLCandidates && !mlMatchingData) {
    return null;
  }

  return (
    <Paper sx={{ mt: 2, p: 1 }}>
      {(() => {
        if (isLoadingMLCandidates) {
          return (
            <Stack direction="row" gap={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">Analyzing room matches...</Typography>
            </Stack>
          );
        }

        if (suggestions.length === 0) {
          return <Typography variant="body2">No suggestions available</Typography>;
        }

        return (
          <Stack spacing={0.5}>
            <Typography>
              <strong>AI suggestions:</strong>
            </Typography>
            {topSuggestions.map(renderSuggestion)}
            {remainingSuggestions.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">Show more ({remainingSuggestions.length})</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0.5 }}>
                  <Stack spacing={0.5}>{remainingSuggestions.map(renderSuggestion)}</Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        );
      })()}
    </Paper>
  );
}
